import { Fragment, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import * as yup from "yup";
import { STATUS } from "../../utils/constants";
import {
  setShowKeyboard,
  setKeyboardConfiguration,
  getCustomerData,
} from "../../redux/slices";

import { useForm} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Spinner } from "../UI/Spinner";
import LoyaltyRewardModal from "./LoyaltyRewardModal";
import { useTranslation } from "react-i18next";
import { useKeyboard } from "../../context/KeyboardContext";
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  composePhone,
  detectPhoneCountry,
  isValidEmail,
  isValidNsn,
  sanitizeEmail,
  toNsn,
} from "../../utils/phoneHelper";

const LoyaltyDetailsModal = ({ openStatus, closeFunc, setLoyaltyValue }) => {
  const dispatch = useDispatch();
  const { customerDataStatus, customerDataError } = useSelector(
    (state) => state.loyalty
  );
  const { orderTotalAmount } = useSelector((state) => state.orders);
  const [countryCode, setCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  // Holds the national number alone — the selector supplies the dial code.
  const [nationalNumber, setNationalNumber] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [openRewardModal, setOpenRewardModal] = useState(false);
  const [rewardData, setRewardData] = useState({});
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [isCheckingCustomer, setIsCheckingCustomer] = useState(false);
  const { setSetSharedInput } = useKeyboard();

  const { t } = useTranslation(["OrderPayment", "Common"]);

  const schema = useMemo(() => {
    const emailRule = yup
      .string()
      .test("valid-email", t("Common:schema.invalid.email"), (value) =>
        value ? isValidEmail(value) : true
      );

    return yup
      .object()
      .shape({
        phoneNumber: yup
          .string()
          .required(t("Common:schema.required.phone"))
          .test("valid-phone", t("Common:schema.invalid.phone"), (value) =>
            isValidNsn(toNsn(value, countryCode), countryCode)
          ),
        email: isExistingCustomer
          ? emailRule
          : emailRule.required(t("Common:schema.required.email")),
        firstName: yup.string(),
        lastName: yup.string(),
        giftcardCode: yup.string(),
      })
      .required();
  }, [isExistingCustomer, countryCode, t]);

  // The on-screen keyboard hands back the whole string at once, so the cap has
  // to live in the setter — a maxLength on the input would not see it.
  const onPhoneChange = (value) => {
    // A pasted full number carries its own dial code; follow it.
    const detected = detectPhoneCountry(value);
    const country = detected || countryCode;
    if (detected && detected !== countryCode) setCountryCode(detected);
    setNationalNumber(toNsn(value, country));
  };

  const onCountryChange = (nextCode) => {
    setCountryCode(nextCode);
    // Re-cap against the new country, which may be shorter.
    setNationalNumber((current) => toNsn(current, nextCode));
  };

  // What the API is sent, and what the schema and lookup validate against.
  const phoneNumber = composePhone(nationalNumber, countryCode);
  const isPhoneComplete = isValidNsn(nationalNumber, countryCode);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      phoneNumber: "",
      email: "",
      firstName: "",
      lastName: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    setValue("phoneNumber", phoneNumber);
    setValue("email", email);
    setValue("firstName", firstName);
    setValue("lastName", lastName);
  }, [phoneNumber, email, firstName, lastName]);

  // Debounced lookup. Without this the on-screen keyboard fires one request per
  // digit, and a slow early response can land after — and overwrite — a later
  // one, so the abort() on cleanup matters as much as the timer.
  useEffect(() => {
    if (!isPhoneComplete) {
      setIsExistingCustomer(false);
      setIsCheckingCustomer(false);
      return;
    }

    setIsCheckingCustomer(true);
    let promise;
    // An aborted request settles asynchronously, by which point the next lookup
    // may already be in flight. Ignore superseded results entirely.
    let cancelled = false;

    const timer = setTimeout(() => {
      promise = dispatch(
        getCustomerData({ phoneNumber, amount: orderTotalAmount })
      );

      promise
        .unwrap()
        .then((data) => {
          if (cancelled) return;
          setIsExistingCustomer(!!data.isExistingMember);
          if (data.firstName) setFirstName(data.firstName);
          if (data.lastName) setLastName(data.lastName);
          if (data.email) setEmail(data.email);
        })
        .catch(() => {
          if (cancelled) return;
          setIsExistingCustomer(false);
        })
        .finally(() => {
          if (cancelled) return;
          setIsCheckingCustomer(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (promise) promise.abort();
    };
  }, [phoneNumber, isPhoneComplete, dispatch, orderTotalAmount]);

  const resetFunc = (isSuccess = false) => {
    setValue("phoneNumber", "");
    setValue("email", "");
    setValue("firstName", "");
    setValue("lastName", "");
    setNationalNumber("");
    setEmail("");
    setFirstName("");
    setLastName("");
    closeFunc(isSuccess);
  };

  const onSubmit = (formData) => {
    if (isCheckingCustomer) return;

    const { firstName, lastName, ...details } = formData;

    // The debounced effect looks up by phone only. When no email was entered
    // that request is identical to this one, so reuse its result instead of
    // paying for a second round-trip on the critical path.
    if (!details.email) {
      if (customerDataStatus !== STATUS.RESOLVED) return;
      setRewardData(formData);
      setOpenRewardModal(true);
      return;
    }

    dispatch(getCustomerData({ ...details, amount: orderTotalAmount }))
      .unwrap()
      .then(() => {
        setRewardData(formData);
        setOpenRewardModal(true);
      })
      .catch(() => {
        // customerDataError is rendered above the form.
      });
  };

  return (
    <Transition appear show={openStatus} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          // setShowError(false);
          // setEnteredPin("");
          // closeFunc();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-bold text-center leading-6 text-gray-900"
                >
                  {t("OrderPayment:header.loyalty")}
                </Dialog.Title>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {customerDataError && (
                    <p className="text-red-500 text-center">
                      {customerDataError}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-x-5">
                    <div className="mt-2 deleteModal flex justify-center ">
                      <div className="relative">
                        <label className="text-center block ">
                          {t("OrderPayment:paymentchannel_modal.label.phone")}
                        </label>

                        <div className="flex">
                          <select
                            value={countryCode}
                            onChange={(e) => onCountryChange(e.target.value)}
                            className="h-[50px] border border-secondary border-r-0 rounded-l py-2 pl-2 pr-1 bg-white"
                            aria-label={t("Common:label.country")}
                          >
                            {Object.values(PHONE_COUNTRIES).map((country) => (
                              <option key={country.code} value={country.code}>
                                +{country.dialCode}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={nationalNumber}
                            inputMode="numeric"
                            maxLength={PHONE_COUNTRIES[countryCode]?.nsnLength}
                            placeholder={
                              PHONE_COUNTRIES[countryCode]?.example ||
                              t("Common:keyboard.placeholder.customer.phone")
                            }
                            className=" w-[100%]  h-[50px] border border-secondary rounded-r py-2 px-3"
                            onChange={(e) => onPhoneChange(e.target.value)}
                            onClick={() => {
                              dispatch(setShowKeyboard(true));
                              dispatch(
                                setKeyboardConfiguration({
                                  type: "numeric",
                                  sharedInput: nationalNumber,
                                  placeHolder: t(
                                    "Common:keyboard.placeholder.customer.phone"
                                  ),
                                })
                              );
                              setSetSharedInput(() => onPhoneChange);
                            }}
                          />
                        </div>
                        {isCheckingCustomer && (
                          <div className="absolute right-2 top-[50%] transform -translate-y-1/2">
                            <Spinner
                              className="!w-5 !h-5 text-blue-500"
                              status={STATUS.PENDING}
                            />
                          </div>
                        )}
                        {!isCheckingCustomer &&
                          !isExistingCustomer &&
                          customerDataStatus === STATUS.RESOLVED &&
                          isPhoneComplete && (
                            <p className="text-red-700 text-sm text-center">
                              {t(
                                "OrderPayment:paymentchannel_modal.errors.member_not_found"
                              )}
                            </p>
                          )}
                        {!isExistingCustomer &&
                          !isPhoneComplete &&
                          errors?.phoneNumber?.message && (
                            <p className="text-red-500 text-center">
                              {errors?.phoneNumber?.message}
                            </p>
                          )}
                      </div>
                    </div>
                    <div className="mt-2 deleteModal flex justify-center">
                      <div>
                        <label className="text-center block">
                          {t("OrderPayment:paymentchannel_modal.label.email")}
                        </label>

                        <input
                          type="email"
                          value={email}
                          maxLength={254}
                          placeholder={t(
                            "Common:keyboard.placeholder.customer.email"
                          )}
                          className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                          onChange={(e) => setEmail(sanitizeEmail(e.target.value))}
                          onClick={() => {
                            dispatch(setShowKeyboard(true));
                            dispatch(
                              setKeyboardConfiguration({
                                type: "alphaNumeric",
                                sharedInput: email,
                                placeHolder: t(
                                  "Common:keyboard.placeholder.customer.email"
                                ),
                              })
                            );
                            setSetSharedInput(() => (x) =>
                              setEmail(sanitizeEmail(x))
                            );
                          }}
                        />
                        {!isExistingCustomer && errors?.email?.message && (
                          <p className="text-red-500 text-center">
                            {errors?.email?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-5">
                    <div className="mt-2 deleteModal flex justify-center">
                      <div>
                        <label className="text-center block">
                          {t("Common:label.name.first")}
                        </label>

                        <input
                          type="text"
                          value={firstName}
                          placeholder={t(
                            "Common:keyboard.placeholder.customer.firstName"
                          )}
                          className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                          onClick={() => {
                            dispatch(setShowKeyboard(true));
                            dispatch(
                              setKeyboardConfiguration({
                                type: "alphaNumeric",
                                sharedInput: firstName,
                                placeHolder: t(
                                  "Common:keyboard.placeholder.customer.firstName"
                                ),
                                // setSharedInput: (x) => {
                                //   setFirstName(x);
                                // },
                              })
                            );
                            setSetSharedInput(() => setFirstName);
                          }}
                        />
                        {errors?.firstName?.message && (
                          <p className="text-red-500 text-center">
                            {errors?.firstName?.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 deleteModal flex justify-center">
                      <div>
                        <label className="text-center block">
                          {t("Common:label.name.last")}
                        </label>

                        <input
                          type="text"
                          value={lastName}
                          placeholder={t(
                            "Common:keyboard.placeholder.customer.lastName"
                          )}
                          className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                          onClick={() => {
                            dispatch(setShowKeyboard(true));
                            dispatch(
                              setKeyboardConfiguration({
                                type: "alphaNumeric",
                                sharedInput: lastName,
                                placeHolder: t(
                                  "Common:keyboard.placeholder.customer.lastName"
                                ),
                                // setSharedInput: (x) => {
                                //   setLastName(x);
                                // },
                              })
                            );
                            setSetSharedInput(() => setLastName);
                          }}
                        />
                        {errors?.lastName?.message && (
                          <p className="text-red-500 text-center">
                            {errors?.lastName?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        resetFunc(false);
                      }}
                      className=" inline-flex justify-center rounded-md border border-transparent bg-red-700 px-4 py-2 text-sm font-medium text-white mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      {t("Common:button.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isCheckingCustomer ||
                        customerDataStatus === STATUS.PENDING
                      }
                      className=" ml-2 inline-flex items-center justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      <span>{t("Common:button.submit")}</span>
                      <Spinner
                        className="!w-3 !h-3 mx-1 text-white fill-blue-800"
                        status={customerDataStatus}
                      />
                    </button>
                  </div>
                </form>
                <LoyaltyRewardModal
                  openStatus={openRewardModal}
                  closeFunc={() => {
                    setOpenRewardModal(false);
                  }}
                  closeParent={() => {
                    resetFunc(true);
                  }}
                  rewardData={rewardData}
                  setLoyaltyValue={setLoyaltyValue}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
export default LoyaltyDetailsModal;
